import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { validateBusinessWebsite } from "../lib/urls";
import AvailabilityPicker from "../components/AvailabilityPicker";
import StripeConnectPanel from "../components/StripeConnectPanel";
import KycPayoutBanner from "../components/KycPayoutBanner";
import AiDescriptionButtons from "../components/AiDescriptionButtons";
import VerifiedBadge from "../components/VerifiedBadge";
import WithdrawPanel from "../components/WithdrawPanel";
import OnboardingChecklist from "../components/OnboardingChecklist";
import BusinessCalendar from "../components/BusinessCalendar";
import { useSeo } from "../lib/seo";
import { fetchCategories } from "../lib/categories";
import ListingChecklist from "../components/ListingChecklist";
import BusinessMediaManager from "../components/BusinessMediaManager";
import ServiceCoverPicker from "../components/ServiceCoverPicker";
import SpecialtyPicker from "../components/SpecialtyPicker";
import BookingQuestionsEditor from "../components/BookingQuestionsEditor";
import BookingAnswersView from "../components/BookingAnswersView";
import BookingRequestsPanel from "../components/BookingRequestsPanel";
import { money } from "../lib/checkoutQuote";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const empty = {
  title: "", category: "", keywords: "", description: "", price: 100,
  requires_deposit: true, deposit_type: "fixed", downpayment_amount: 20,
  downpayment_refundable: true, duration_minutes: 60, location: "", image_url: "",
  service_location_type: "at_business",
  booking_specialty_id: "", booking_questions: [],
  availability: [{ day: 0, start: "09:00", end: "17:00" }, { day: 1, start: "09:00", end: "17:00" }, { day: 2, start: "09:00", end: "17:00" }, { day: 3, start: "09:00", end: "17:00" }, { day: 4, start: "09:00", end: "17:00" }],
  blackout_dates: [],
};

const inputCls = "w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]";
const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

export default function BusinessDashboard() {
  useSeo({ title: "Business dashboard — BookFreely", path: "/business", noindex: true });
  const { user, authHeader, refreshUser, reconcileSubscription, loading } = useAuth();
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, entries: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  // Iteration 55 — Mobile Services feature flag (fetched from platform/config).
  const [mobileServicesEnabled, setMobileServicesEnabled] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [pubStatus, setPubStatus] = useState(null);
  const [catSuggestion, setCatSuggestion] = useState(null);
  // Iteration 80 — booking questions: the business's confirmed specialties.
  const [specialtyState, setSpecialtyState] = useState(null);
  const [specialtyDraft, setSpecialtyDraft] = useState([]);
  const [specialtyPromptOpen, setSpecialtyPromptOpen] = useState(false);
  const [specialtySaving, setSpecialtySaving] = useState(false);
  const payloadRef = React.useRef(null);
  const nav = useNavigate();

  useEffect(() => { fetchCategories().then(setServiceCategories); }, []);

  useEffect(() => {
    axios.get(`${API}/platform/config`).then((r) => {
      setMobileServicesEnabled(!!r.data?.mobile_services_enabled);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "business") { nav("/"); return; }
    refreshUser();
    // Auto-recovery: if local subscription state looks inactive, ask the
    // backend to check Stripe. Rate-limited both client- and server-side.
    if (reconcileSubscription) { try { reconcileSubscription(); } catch (_) { /* best-effort */ } }
    load();
    // eslint-disable-next-line
  }, [loading, user?.id]);

  // Trial countdown ticker: bumped every minute so the "N days remaining"
  // pill decrements as days pass even if the tab is left open overnight.
  const [nowTick, setNowTick] = useState(Date.now());

  // Real-time-ish sync: refetch bookings/services/balance every 30s + on
  // window focus. Picks up newly-paid bookings from the Stripe webhook without
  // requiring the business owner to manually refresh.
  useEffect(() => {
    if (!user || user.role !== "business") return;
    const onFocus = () => {
      load();
      // On window focus, also re-sync the auth-me record so Stripe-side
      // cancels/pause/re-activations landed via webhook show up immediately —
      // no manual refresh, no waiting for the next 30s tick.
      if (refreshUser) { try { refreshUser(); } catch (_) { /* best-effort */ } }
      if (reconcileSubscription) { try { reconcileSubscription(); } catch (_) { /* best-effort */ } }
    };
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 30000);
    const tick = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
      clearInterval(tick);
    };
  }, [user?.id, refreshUser, reconcileSubscription]);

  const load = async () => {
    try {
      const [s, b, bal, ps] = await Promise.all([
        axios.get(`${API}/business/services`, { headers: authHeader() }),
        axios.get(`${API}/bookings/business`, { headers: authHeader() }),
        axios.get(`${API}/business/balance`, { headers: authHeader() }),
        axios.get(`${API}/business/publication-status`, { headers: authHeader() }),
      ]);
      setServices(s.data); setBookings(b.data); setBalance(bal.data); setPubStatus(ps.data);
    } catch {}
    try {
      const sp = await axios.get(`${API}/business/specialties`, { headers: authHeader() });
      setSpecialtyState(sp.data);
      setSpecialtyDraft(sp.data?.selected || []);
    } catch {}
  };

  const saveSpecialties = async (ids) => {
    setSpecialtySaving(true);
    try {
      await axios.put(`${API}/business/specialties`, { specialties: ids }, { headers: authHeader() });
      toast.success("Specialties saved");
      setSpecialtyPromptOpen(false);
      await load();
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your specialties");
    } finally {
      setSpecialtySaving(false);
    }
  };

  const mySpecialtyOptions = (specialtyState?.options || [])
    .filter((o) => (specialtyState?.selected || []).includes(o.id));

  const subActive = user?.subscription_active;

  // Iteration 56 (Feb 28 2026) — Auto-reconcile with Stripe when the user
  // returns from the Customer Portal. This closes the "displayed as active
  // but really cancelled" gap even before the Stripe webhook lands.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "portal") return;
    (async () => {
      try {
        await axios.post(`${API}/subscription/reconcile`, { force: true }, { headers: authHeader() });
      } catch (e) {
        // Best-effort — webhook is the primary source of truth.
      }
      try { if (refreshUser) await refreshUser(); } catch (e) { /* noop */ }
      // Strip the `?from=portal` param so a page refresh doesn't re-reconcile.
      const url = new URL(window.location.href);
      url.searchParams.delete("from");
      window.history.replaceState({}, "", url.toString());
      toast.success("Subscription updated");
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBillingPortal = async () => {
    try {
      const r = await axios.post(`${API}/subscription/portal`, {}, { headers: authHeader() });
      if (r?.data?.url) {
        window.location.assign(r.data.url);
      } else {
        toast.error("Couldn't open billing portal. Please contact support.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    }
  };

  // Suggest the canonical category from the service's OWN title + description
  // (never from the signup category or keywords), and preselect it.
  useEffect(() => {
    const title = (form.title || "").trim();
    const description = (form.description || "").trim();
    if (!showForm || (!title && !description)) { setCatSuggestion(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/services/classify`, { title, description }, { headers: authHeader() });
        const sug = r.data?.suggestion || null;
        setCatSuggestion(sug);
        if (sug && !form.category) setForm((f) => (f.category ? f : { ...f, category: sug.slug }));
      } catch { setCatSuggestion(null); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.description, showForm]);

  const categoryConflict =
    catSuggestion && catSuggestion.confidence === "high" && form.category &&
    form.category !== catSuggestion.slug ? catSuggestion : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!subActive) { toast.error("Subscription required"); nav("/business/subscribe"); return; }
    if (!form.category) {
      toast.error(catSuggestion
        ? `Pick a category — we suggest ${catSuggestion.label}`
        : "Pick a category for this service from the list");
      return;
    }
    if (categoryConflict) {
      toast.error(`This service appears to belong in ${categoryConflict.label}. Use ${categoryConflict.label}?`);
      return;
    }
    if ((form.description || "").trim().length < 80) {
      toast.error("Describe what's included in at least 80 characters so this service can be published");
      return;
    }
    if (!form.image_url) { toast.error("Add a cover image — services need one to appear in search"); return; }
    try {
      const payload = {
        ...form,
        keywords: String(form.keywords || "").split(",").map((k) => k.trim()).filter(Boolean),
        price: parseFloat(form.price),
        downpayment_amount: form.requires_deposit ? parseFloat(form.downpayment_amount || 0) : 0,
        duration_minutes: parseInt(form.duration_minutes),
        requires_deposit: !!form.requires_deposit,
        deposit_type: form.deposit_type || "fixed",
        service_location_type: form.service_location_type || "at_business",
        booking_specialty_id: form.booking_specialty_id || null,
        booking_questions: (form.booking_questions || []).map((q) => ({
          id: q.id || null, label: (q.label || "").trim(), type: q.type,
          required: !!q.required, choices: q.choices || [], help: q.help || null,
        })),
      };
      const badQ = (payload.booking_questions || []).find((q) => !q.label);
      if (badQ) { toast.error("Every booking question needs a label (or remove it)."); return; }
      payloadRef.current = payload;
      if (editingId) {
        await axios.patch(`${API}/services/${editingId}`, payload, { headers: authHeader() });
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/services`, payload, { headers: authHeader() });
        toast.success("Service listed");
      }
      setForm(empty); setShowForm(false); setEditingId(null);
      load();
    } catch (e) {
      // 409 = we'd be replacing questions the business already approved.
      // Ask, then retry with the explicit confirmation.
      if (e?.response?.status === 409 && editingId) {
        if (window.confirm(`${e.response.data?.detail}\n\nReplace them?`)) {
          try {
            await axios.patch(`${API}/services/${editingId}`,
              { ...payloadRef.current, confirm_replace_questions: true }, { headers: authHeader() });
            toast.success("Service updated");
            setForm(empty); setShowForm(false); setEditingId(null);
            load();
            return;
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Failed");
            return;
          }
        }
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (s) => {
    setForm({
      ...empty,
      ...s,
      category: s.category_slug || s.category || "",
      keywords: Array.isArray(s.keywords) ? s.keywords.join(", ") : (s.keywords || ""),
      requires_deposit: s.requires_deposit !== false,
      deposit_type: s.deposit_type || "fixed",
      booking_specialty_id: s.booking_specialty_id || "",
      booking_questions: Array.isArray(s.booking_questions) ? s.booking_questions : [],
    });
    setEditingId(s.id);
    setShowForm(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this service?")) return;
    await axios.delete(`${API}/services/${id}`, { headers: authHeader() });
    toast.success("Service removed");
    load();
  };

  const act = async (bid, action, b, extra = {}) => {
    try {
      await axios.post(`${API}/bookings/${bid}/action`, { action, ...extra }, { headers: authHeader() });
      toast.success(`Booking ${action}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Iteration 53 — payment-required booking actions.
  const retryBalance = async (bid) => {
    try {
      const r = await axios.post(`${API}/bookings/${bid}/retry-balance-charge`, {}, { headers: authHeader() });
      if (r.data?.remaining_balance_status === "captured") toast.success("Remaining balance charged successfully");
      else toast.error(`Retry failed: ${r.data?.last_error || "card declined"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };
  const markInPerson = async (bid) => {
    const note = window.prompt("Optional note (e.g. paid cash / paid via Venmo):", "") || "";
    try {
      await axios.post(`${API}/bookings/${bid}/mark-collected-in-person`, { note }, { headers: authHeader() });
      toast.success("Marked as collected in person");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const cancelPerPolicy = async (bid) => {
    const reason = window.prompt("Reason (visible to the customer):", "") || "";
    if (!window.confirm("Cancel this booking? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/bookings/${bid}/cancel-per-policy`, { reason }, { headers: authHeader() });
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [acceptModal, setAcceptModal] = useState(null);  // booking object

  if (loading || !user) return <div className="App"><Header hideSearch /><div className="max-w-7xl mx-auto p-12" style={{ color: "var(--gf-text-muted)" }}>Loading...</div></div>;

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Business dashboard</div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <h1 className="font-display text-4xl" style={{ color: "var(--gf-text)" }}>{user.business_name || user.full_name}</h1>
              {user.stripe_details_submitted && user.stripe_charges_enabled && user.stripe_payouts_enabled && <VerifiedBadge size="md" />}
            </div>
            {user.business_handle && (
              <div
                className="text-sm mt-1"
                style={{ color: "var(--gf-text-muted)" }}
                data-testid="dashboard-business-handle"
              >
                <span style={{ color: "var(--gf-accent)" }}>@{user.business_handle}</span>
                <span className="mx-2 opacity-40">·</span>
                <button
                  type="button"
                  onClick={() => nav("/business/handle")}
                  className="underline opacity-80 hover:opacity-100"
                  data-testid="dashboard-change-handle-btn"
                >
                  Change handle
                </button>
              </div>
            )}
            {user.business_category && (
              <div className="text-xs uppercase tracking-[0.2em] mt-1" style={{ color: "var(--gf-accent)" }}>
                Listed in: {user.business_category}
              </div>
            )}
            {specialtyState?.supported && (specialtyState.selected || []).length > 0 && (
              <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }} data-testid="dashboard-specialties">
                Specialties: {(specialtyState.options || [])
                  .filter((o) => specialtyState.selected.includes(o.id))
                  .map((o) => o.label).join(" · ")}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="rounded-full border px-4 py-2 text-sm"
                 style={{ borderColor: subActive ? "var(--gf-surface-2)" : "var(--gf-border-strong)", color: subActive ? "var(--gf-accent)" : "var(--gf-text-muted)" }}>
              {subActive
                ? (user.subscription_status === "trialing"
                    ? `Free trial · ${user.subscription_tier || "monthly"}`
                    : `Subscription active · ${user.subscription_tier}`)
                : "Subscription inactive"}
            </div>
            <button
              onClick={() => nav("/account")}
              className="rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] hover:opacity-80"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              data-testid="switch-to-customer-btn"
            >
              ← Switch to customer view
            </button>
          </div>
        </div>

        {!subActive && (
          <div data-testid="sub-required-banner" className="mt-6 rounded-2xl border p-6 flex items-center justify-between flex-wrap gap-4"
               style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
            <div>
              <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Activate your subscription to publish services</div>
              <div className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>Your services stay as drafts until your subscription is active. 7-day free trial on the monthly plan. 0% commission on your own and returning customers; a one-time 10% fee (capped at $30) applies only to a new marketplace customer's first booking.</div>
            </div>
            <button data-testid="go-subscribe-btn" onClick={() => nav("/business/subscribe")}
              className="rounded-full font-medium px-6 py-3"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Subscribe</button>
          </div>
        )}
        {subActive && user.subscription_status === "trialing" && user.trial_end_at && (() => {
          const ends = new Date(user.trial_end_at);
          // Use `nowTick` so this recomputes every minute as the ticker fires
          // — that's what keeps "6 days remaining" → "5 days remaining" →
          // "4 days remaining" fresh without a manual page reload.
          const now = new Date(nowTick);
          const daysLeft = Math.max(0, Math.ceil((ends - now) / 86400000));
          const endsFmt = ends.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
          // Trial only ever exists on the Monthly plan now. The next charge is
          // therefore always $19.99 per month. `subscription_tier` guards against
          // legacy trial rows for yearly (should not exist post Feb 23 2026).
          const nextCharge = "$19.99";
          const cadence = "month";
          return (
            <div data-testid="trial-active-banner" className="mt-6 rounded-2xl border p-6 flex items-center justify-between flex-wrap gap-4"
                 style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--gf-accent)" }}>7-Day Free Trial</div>
                <div className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }} data-testid="trial-banner-title">
                  {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
                  Next charge: <b style={{ color: "var(--gf-text)" }} data-testid="trial-next-charge">{nextCharge}</b> per {cadence} on{" "}
                  <b style={{ color: "var(--gf-text)" }} data-testid="trial-banner-end">{endsFmt}</b>.
                  Cancel anytime before then from your billing portal.
                </div>
              </div>
              <button data-testid="open-portal-from-trial-btn" onClick={() => nav("/business/billing")}
                className="rounded-full font-medium px-6 py-3"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Manage billing</button>
            </div>
          );
        })()}

        {subActive && user.subscription_status === "past_due" && (
          <div data-testid="past-due-banner" className="mt-6 rounded-2xl border p-6 flex items-center justify-between flex-wrap gap-4"
               style={{ borderColor: "#fca5a5", background: "#fef2f2" }}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#b91c1c" }}>Payment failed</div>
              <div className="font-display text-xl mt-1" style={{ color: "#7f1d1d" }} data-testid="past-due-title">
                Your last renewal payment didn&rsquo;t go through
              </div>
              <div className="text-sm mt-1" style={{ color: "#7f1d1d" }}>
                Stripe is retrying automatically. Update your card now to keep your listings live — if Stripe gives up, your subscription will cancel and your services will be hidden from search.
              </div>
            </div>
            <button data-testid="open-portal-from-past-due-btn" onClick={openBillingPortal}
              className="rounded-full font-medium px-6 py-3"
              style={{ background: "#b91c1c", color: "white" }}>Update card in Stripe</button>
          </div>
        )}

        <OnboardingChecklist
          user={user}
          services={services}
          onAddService={() => { setEditingId(null); setForm(empty); setShowForm(true); setTimeout(() => { const el = document.getElementById("services-section"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 50); }}
          onEditProfile={() => { const el = document.getElementById("business-profile-editor"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
        />

        {(() => {
          // Iteration 72 (Mar 2026) — while the Mobile Services flag is off we
          // render NOTHING here. The previous "Coming Soon" panel advertised a
          // disabled feature. Flag/state handling below is untouched.
          if (!mobileServicesEnabled) return null;
          const mvState = user.mobile_verification_state || {};
          const acknowledged = !!(mvState.safety_policy_acknowledged);
          const isMobileVerified = !!user?.mobile_verified;
          const pill = isMobileVerified
            ? { label: "Ready for mobile services", color: "var(--gf-success)" }
            : acknowledged
              ? { label: "Awaiting Stripe verification", color: "var(--gf-warning)" }
              : { label: "Not started", color: "var(--gf-text-muted)" };
          return (
            <div data-testid="mv-cta-panel" className="mt-8 rounded-2xl border p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
                 style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Mobile services safety</div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]"
                        style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg)", color: pill.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: pill.color }} />
                    {pill.label}
                  </span>
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
                  Acknowledge the safety policy once to unlock mobile / at-customer-location / in-home services. Once your identity is confirmed during payout setup, these service types activate automatically — no separate review by BookFreely.
                </div>
              </div>
              <button data-testid="mv-cta-btn" onClick={() => nav("/business/mobile-verification")}
                      className="rounded-md px-5 py-2.5 text-sm font-medium"
                      style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                {isMobileVerified ? "Review status" : acknowledged ? "View status" : "Start"}
              </button>
            </div>
          );
        })()}

        <BookingRequestsPanel authHeader={authHeader} onChanged={load} />

        {/* Iteration 83 — default booking method. Read only when a NEW request
            or booking is created, so existing bookings are never altered. */}
        <div className="mt-6 rounded-2xl border p-5" data-testid="booking-method-setting"
             style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            How customers book you
          </div>
          <p className="text-xs mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
            Changing this affects new bookings only — anything already booked stays exactly as it is.
          </p>
          <div className="flex flex-wrap gap-2">
            {[["instant", "Instant booking", "Customers pick an open slot and pay right away."],
              ["request", "Request booking", "Customers propose a time. Nothing is charged until you accept."]].map(([v, label, hint]) => (
              <button key={v} type="button" data-testid={`booking-method-${v}`}
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/business/booking-method`, { booking_method: v }, { headers: authHeader() });
                          toast.success("Saved");
                          if (refreshUser) await refreshUser();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that"); }
                      }}
                      className="text-left rounded-xl border px-4 py-3 max-w-xs"
                      style={{
                        borderColor: (user.booking_method || "instant") === v ? "var(--gf-accent)" : "var(--gf-border-strong)",
                        background: (user.booking_method || "instant") === v ? "var(--gf-bg-soft)" : "transparent",
                      }}>
                <div className="text-sm" style={{ color: "var(--gf-text)" }}>{label}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{hint}</div>
              </button>
            ))}
          </div>
        </div>

        <BusinessProfileEditor user={user} authHeader={authHeader} refreshUser={refreshUser} />

        {/* Iteration 80 — non-blocking specialty prompt. Nothing is guessed or
            auto-saved, and the business can dismiss it and keep working. */}
        {specialtyState?.needs_selection && (
          <div className="mt-6 rounded-2xl border p-5" data-testid="specialty-prompt"
               style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface)" }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                  Optional · improves your booking form
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--gf-text)" }}>
                  What do you specialize in? We&rsquo;ll suggest the right booking questions for new services.
                </div>
              </div>
              {!specialtyPromptOpen && (
                <div className="flex gap-2">
                  <button type="button" data-testid="specialty-prompt-open"
                          onClick={() => setSpecialtyPromptOpen(true)}
                          className="rounded-full px-4 py-2 text-xs font-medium"
                          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                    Choose specialties
                  </button>
                  <button type="button" data-testid="specialty-prompt-dismiss"
                          onClick={() => setSpecialtyState((s) => ({ ...s, needs_selection: false }))}
                          className="rounded-full border px-4 py-2 text-xs"
                          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                    Not now
                  </button>
                </div>
              )}
            </div>
            {specialtyPromptOpen && (
              <div className="mt-4">
                <SpecialtyPicker options={specialtyState.options || []} value={specialtyDraft}
                                 onChange={setSpecialtyDraft} testIdPrefix="specialty-prompt" />
                <div className="mt-3 flex gap-2">
                  <button type="button" data-testid="specialty-prompt-save" disabled={specialtySaving || !specialtyDraft.length}
                          onClick={() => saveSpecialties(specialtyDraft)}
                          className="rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50"
                          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                    {specialtySaving ? "Saving…" : "Save specialties"}
                  </button>
                  <button type="button" onClick={() => setSpecialtyPromptOpen(false)}
                          className="rounded-full border px-4 py-2 text-xs"
                          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {specialtyState?.supported && !specialtyState?.needs_selection && (
          <div className="mt-6 rounded-2xl border p-5" data-testid="specialty-settings"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
              Your specialties
            </div>
            <p className="text-xs mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
              Used to suggest booking questions. Changing these never edits questions already on a service.
            </p>
            <SpecialtyPicker options={specialtyState.options || []} value={specialtyDraft}
                             onChange={setSpecialtyDraft} testIdPrefix="specialty-settings" />
            <button type="button" data-testid="specialty-settings-save" disabled={specialtySaving}
                    onClick={() => saveSpecialties(specialtyDraft)}
                    className="mt-3 rounded-full border px-4 py-2 text-xs disabled:opacity-50"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
              {specialtySaving ? "Saving…" : "Save specialties"}
            </button>
          </div>
        )}

        <KycPayoutBanner user={user} refreshUser={refreshUser} />

        <StripeConnectPanel user={user} refreshUser={refreshUser} />

        <WithdrawPanel user={user} />

        <BusinessStats bookings={bookings} services={services} balance={balance.balance} />

        <AnalyticsPanel authHeader={authHeader} user={user} />

        <BusinessCalendar />

        <SubscriptionPanel user={user} authHeader={authHeader} refreshUser={refreshUser} />

        <AccountSettingsPanel user={user} authHeader={authHeader} refreshUser={refreshUser} />

        <ListingChecklist status={pubStatus} />
        <BusinessMediaManager user={user} authHeader={authHeader} status={pubStatus}
                              onChanged={async () => { await load(); if (refreshUser) await refreshUser(); }} />

        <div id="services-section" className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>Your services</h2>
              <button data-testid="new-service-btn" onClick={() => { setForm(empty); setEditingId(null); setShowForm(!showForm); }}
                className="rounded-full font-medium px-5 py-2"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                {showForm ? "Close" : "+ New service"}
              </button>
            </div>

            {showForm && (
              <form onSubmit={submit} data-testid="service-form" className="mt-5 rounded-2xl border p-6 grid sm:grid-cols-2 gap-4"
                    style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
                <Field label="Title"><input required id="svc-title" data-testid="svc-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Category">
                  <select id="svc-category" required data-testid="svc-category-select"
                          value={form.category}
                          onChange={(e) => setForm({ ...form, category: e.target.value })}
                          className={inputCls} style={inputStyle}>
                    <option value="">Select a category…</option>
                    {serviceCategories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </select>
                  <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                    This is the category customers browse. Use keywords below for specifics like &ldquo;detailing&rdquo; or &ldquo;wheel alignment&rdquo;.
                  </div>
                  {categoryConflict ? (
                    <div data-testid="category-conflict-warning" className="mt-2 rounded-lg border px-3 py-2 text-xs"
                         style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface-2)", color: "var(--gf-text)" }}>
                      This service appears to belong in <b>{categoryConflict.label}</b>. Use {categoryConflict.label}?
                      <button type="button" data-testid="use-suggested-category-btn"
                              onClick={() => setForm({ ...form, category: categoryConflict.slug })}
                              className="ml-2 rounded-full px-3 py-1 text-[11px] font-medium"
                              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                        Use {categoryConflict.label}
                      </button>
                    </div>
                  ) : !form.category && catSuggestion ? (
                    <div data-testid="category-suggestion-hint" className="mt-2 text-xs" style={{ color: "var(--gf-text-soft)" }}>
                      Suggested: <b>{catSuggestion.label}</b>
                    </div>
                  ) : null}
                </Field>
                <Field label="Description" full><textarea required id="svc-description" data-testid="svc-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Service price ($)"><input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Duration (min)"><input required type="number" min="5" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Location note (optional)">
                  <input id="svc-location-note" data-testid="svc-location-note-input" value={form.location || ""}
                         onChange={(e) => setForm({ ...form, location: e.target.value })}
                         className={inputCls} style={inputStyle} placeholder="e.g. Suite 200 · Studio B" />
                  <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                    Shown on the listing next to your city. Leave blank to show your city only.
                  </div>
                </Field>
                <Field label="Keywords (optional, comma separated)" full>
                  <input id="svc-keywords" data-testid="svc-keywords-input" value={form.keywords || ""}
                         onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                         className={inputCls} style={inputStyle} placeholder="detailing, ceramic coating, wheel alignment" />
                </Field>
                <Field label="Cover image (required)" full>
                  <ServiceCoverPicker
                    value={form.image_url || ""}
                    onChange={(url) => setForm({ ...form, image_url: url })}
                    gallery={pubStatus?.gallery_images || []}
                    authHeader={authHeader}
                  />
                </Field>

                <Field label="Service location" full>
                  {mobileServicesEnabled ? (
                    <>
                      <select
                        data-testid="service-location-type-select"
                        value={form.service_location_type || "at_business"}
                        onChange={(e) => setForm({ ...form, service_location_type: e.target.value })}
                        className={inputCls}
                        style={inputStyle}
                      >
                        <option value="at_business">At my business</option>
                        <option value="mobile">Mobile / I travel to the customer</option>
                        <option value="in_home">In customer&apos;s home</option>
                        <option value="either">Either (I&apos;m flexible)</option>
                      </select>
                      {form.service_location_type && form.service_location_type !== "at_business" && (
                        !user?.mobile_verified ? (
                          <div data-testid="mv-required-warning" className="mt-2 rounded-md border px-3 py-2 text-xs"
                               style={{ borderColor: "var(--gf-warning)", background: "var(--gf-surface)", color: "var(--gf-text)" }}>
                            Mobile / in-home services activate automatically once your identity is confirmed during payout setup. <Link to="/business/mobile-verification" className="underline">Review safety policy →</Link>
                          </div>
                        ) : (
                          <div className="mt-2 text-[11px]" style={{ color: "var(--gf-success)" }}>
                            ✓ Ready for mobile services — customers will see the safety badge on this listing.
                          </div>
                        )
                      )}
                    </>
                  ) : (
                    // Iteration 55 — Mobile Services temporarily disabled pre-launch.
                    // Only "at my business" is bookable today; the dropdown is
                    // replaced with a static field. Existing mobile service rows
                    // remain in the DB unchanged. (Iter 72: the "Coming Soon"
                    // note that used to sit here was removed.)
                    <>
                      <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)", color: "var(--gf-text)" }} data-testid="service-location-locked">
                        At my business
                      </div>
                    </>
                  )}
                </Field>

                <div className="sm:col-span-2 rounded-xl border p-4" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }} data-testid="deposit-controls">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Deposit policy</div>
                      <div className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
                        Charge customers a deposit upfront, or let them request a booking with no money down.
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none" style={{ color: "var(--gf-text)" }}>
                      <input
                        data-testid="requires-deposit-toggle"
                        type="checkbox"
                        checked={!!form.requires_deposit}
                        onChange={(e) => setForm({ ...form, requires_deposit: e.target.checked })}
                        className="w-4 h-4"
                        style={{ accentColor: "var(--gf-accent)" }}
                      />
                      <span className="text-sm">Require deposit at booking</span>
                    </label>
                  </div>

                  {form.requires_deposit ? (
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <Field label="Deposit type">
                        <select
                          data-testid="deposit-type-select"
                          value={form.deposit_type}
                          onChange={(e) => setForm({ ...form, deposit_type: e.target.value })}
                          className={inputCls}
                          style={inputStyle}
                        >
                          <option value="fixed">Fixed dollar amount ($)</option>
                          <option value="percent">Percentage of price (%)</option>
                        </select>
                      </Field>
                      <Field label={form.deposit_type === "percent" ? "Deposit (% of price)" : "Deposit ($)"}>
                        <input
                          data-testid="deposit-amount-input"
                          required
                          type="number"
                          step={form.deposit_type === "percent" ? "1" : "0.01"}
                          min="0"
                          max={form.deposit_type === "percent" ? "100" : undefined}
                          value={form.downpayment_amount}
                          onChange={(e) => setForm({ ...form, downpayment_amount: e.target.value })}
                          className={inputCls}
                          style={inputStyle}
                        />
                      </Field>
                      <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer" style={{ color: "var(--gf-text)" }}>
                        <input data-testid="svc-refundable-checkbox" type="checkbox" checked={form.downpayment_refundable} onChange={(e) => setForm({ ...form, downpayment_refundable: e.target.checked })} className="w-4 h-4" style={{ accentColor: "var(--gf-accent)" }} />
                        <span className="text-sm">Deposit is refundable</span>
                      </label>
                    </div>
                  ) : (
                    <div className="mt-3 text-xs" style={{ color: "var(--gf-text-muted)" }} data-testid="no-deposit-hint">
                      Customers will be able to <b style={{ color: "var(--gf-text)" }}>request a booking</b> without paying anything upfront. You'll see their request as <b style={{ color: "var(--gf-text)" }}>pending</b> in your bookings list — accept or decline it from there. The full ${Number(form.price || 0).toFixed(2)} is collected by you at the appointment.
                    </div>
                  )}
                </div>
                <AvailabilityPicker
                  availability={form.availability || []}
                  blackoutDates={form.blackout_dates || []}
                  onChange={({ availability, blackout_dates }) => setForm({ ...form, availability, blackout_dates })}
                />
                <BookingQuestionsEditor
                  mySpecialties={mySpecialtyOptions}
                  specialtyId={form.booking_specialty_id || ""}
                  questions={form.booking_questions || []}
                  authHeader={authHeader}
                  onChange={({ specialtyId, questions }) =>
                    setForm((f) => ({ ...f, booking_specialty_id: specialtyId, booking_questions: questions }))}
                />
                <div className="sm:col-span-2 flex gap-3">
                  <button data-testid="svc-save-btn" type="submit"
                    className="rounded-full font-medium px-6 py-2.5"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                    {editingId ? "Save" : "Publish service"}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(empty); }}
                    className="rounded-full border px-6 py-2.5"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>Cancel</button>
                </div>
              </form>
            )}

            <div className="mt-5 space-y-3" data-testid="services-list">
              {services.length === 0 ? (
                <div className="text-sm p-6 border rounded-xl text-center" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
                  No services yet — click <strong style={{ color: "var(--gf-text)" }}>+ New service</strong> above to list your first one. There's no limit on how many you can add.
                </div>
              ) : services.map((s) => {
                const hasDeposit = s.requires_deposit !== false && Number(s.downpayment_amount || 0) > 0;
                const depositLabel = !hasDeposit
                  ? "no deposit"
                  : s.deposit_type === "percent"
                    ? `${Number(s.downpayment_amount).toFixed(0)}% deposit · ${s.downpayment_refundable ? "refundable" : "non-refundable"}`
                    : `deposit $${Number(s.downpayment_amount || 0).toFixed(2)} · ${s.downpayment_refundable ? "refundable" : "non-refundable"}`;
                const svcStatus = (pubStatus?.services || []).find((x) => x.id === s.id);
                return (
                  <div key={s.id} className="rounded-xl border p-5 flex items-center gap-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 flex-wrap" style={{ color: "var(--gf-text-muted)" }}>
                        <span>{s.category}</span>
                        {svcStatus && !svcStatus.published && (
                          <span data-testid={`svc-not-published-${s.id}`} className="normal-case tracking-normal rounded-full px-2 py-0.5 text-[10px]"
                                style={{ border: "1px solid var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
                            Not visible — {(svcStatus.missing || []).map((m) => m.code.replace("_", " ")).join(", ")}
                          </span>
                        )}
                      </div>
                      <div className="font-display text-xl mt-0.5" style={{ color: "var(--gf-text)" }}>{s.title}</div>
                      <div className="text-sm mt-1" style={{ color: "var(--gf-text)" }}>
                        <span className="font-semibold">${Number(s.price || 0).toFixed(2)}</span>
                        <span className="ml-2" style={{ color: "var(--gf-text-muted)" }}>· {depositLabel} · {s.duration_minutes} min</span>
                      </div>
                    </div>
                    <button onClick={() => startEdit(s)} className="text-sm hover:opacity-80" style={{ color: "var(--gf-text-soft)" }}>Edit</button>
                    <button data-testid={`remove-svc-${s.id}`} onClick={() => remove(s.id)} className="text-sm hover:text-red-500" style={{ color: "var(--gf-text-muted)" }}>Remove</button>
                  </div>
                );
              })}
              {services.length > 0 && !showForm && (
                <button data-testid="add-another-service-btn" onClick={() => { setForm(empty); setEditingId(null); setShowForm(true); }}
                  className="w-full mt-2 rounded-xl border-2 border-dashed p-4 text-sm hover:opacity-80 transition-opacity"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                  + Add another service ({services.length} listed — no limit)
                </button>
              )}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>Upcoming calendar</h2>
            <UpcomingList bookings={bookings} />
            <h2 className="font-display text-2xl mt-8" style={{ color: "var(--gf-text)" }}>Bookings</h2>
            <div className="mt-5 space-y-3" data-testid="bookings-list">
              {bookings.length === 0 ? (
                <div className="text-sm p-6 border rounded-xl" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>No bookings yet.</div>
              ) : bookings.map((b) => {
                const hasDeposit = Number(b.downpayment_amount || 0) > 0;
                const statusStyles = {
                  pending: { borderColor: "var(--gf-surface-2)", color: "var(--gf-accent)", bg: "var(--gf-surface-2)" },
                  pending_payment: { borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)", bg: "transparent" },
                  payment_required: { borderColor: "#b8862a", color: "#b8862a", bg: "rgba(184,134,42,0.08)" },
                  confirmed: { borderColor: "var(--gf-surface)", color: "var(--gf-accent)", bg: "var(--gf-surface)" },
                  completed: { borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)", bg: "transparent" },
                  declined: { borderColor: "var(--gf-border)", color: "var(--gf-text-muted)", bg: "transparent" },
                  cancelled: { borderColor: "var(--gf-border)", color: "var(--gf-text-muted)", bg: "transparent" },
                  refunded: { borderColor: "var(--gf-border)", color: "var(--gf-text-muted)", bg: "transparent" },
                };
                const ss = statusStyles[b.status] || statusStyles.pending_payment;
                return (
                  <div key={b.id} className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid={`booking-${b.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-base truncate" style={{ color: "var(--gf-text)" }}>{b.service_title}</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{b.customer_name} · {new Date(b.scheduled_at).toLocaleString()}</div>
                      </div>
                      <span data-testid={`booking-status-${b.id}`} className="text-[10px] uppercase tracking-[0.2em] border rounded-full px-2 py-1 whitespace-nowrap" style={{ borderColor: ss.borderColor, color: ss.color, background: ss.bg }}>
                        {b.status === "pending" ? "Awaiting your reply" : b.status === "payment_required" ? "Payment required" : b.status.replace("_", " ")}
                      </span>
                    </div>
                    {b.notes && <div className="text-sm mt-2 italic" style={{ color: "var(--gf-text-muted)" }}>"{b.notes}"</div>}
                    <BookingAnswersView answers={b.booking_answers} authHeader={authHeader} testId={`booking-answers-${b.id}`} />
                    <div className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
                      {hasDeposit
                        ? <>Deposit: ${Number(b.downpayment_amount).toFixed(2)} · {b.downpayment_refundable ? "refundable" : "non-refundable"}</>
                        : <>No deposit · ${Number(b.service_price_snapshot || 0).toFixed(2)} due at appointment</>
                      }
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {b.status === "pending" && (
                        <>
                          <button data-testid={`accept-${b.id}`} onClick={() => setAcceptModal(b)} className="text-xs rounded-full px-3 py-1.5 font-medium" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Accept</button>
                          <button data-testid={`decline-pending-${b.id}`} onClick={() => act(b.id, "decline", b)} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Decline</button>
                        </>
                      )}
                      {b.status === "payment_required" && (
                        <>
                          <div className="basis-full text-xs rounded-md px-2 py-1.5 mb-1" style={{ background: "rgba(184,134,42,0.08)", color: "#b8862a", border: "1px solid rgba(184,134,42,0.35)" }} data-testid={`payment-required-notice-${b.id}`}>
                            Automatic remaining-balance charge failed after 3 attempts. Choose an action below — we will NOT auto-cancel or refund.
                            {b.remaining_balance_last_error && <span className="block opacity-80 mt-0.5">Last error: {b.remaining_balance_last_error}</span>}
                          </div>
                          <button data-testid={`retry-balance-${b.id}`} onClick={() => retryBalance(b.id)} className="text-xs rounded-full px-3 py-1.5 font-medium" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Retry saved card</button>
                          <button data-testid={`mark-in-person-${b.id}`} onClick={() => markInPerson(b.id)} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Mark collected in person</button>
                          <button data-testid={`cancel-per-policy-${b.id}`} onClick={() => cancelPerPolicy(b.id)} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>Cancel per policy</button>
                        </>
                      )}
                      {b.status === "confirmed" && (
                        <>
                          <button data-testid={`complete-${b.id}`} onClick={() => act(b.id, "complete", b)} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Mark complete</button>
                          <button data-testid={`decline-${b.id}`} onClick={() => act(b.id, "decline", b)} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Cancel</button>
                          {hasDeposit && b.downpayment_refundable && (
                            <button data-testid={`mark-refund-${b.id}`} onClick={() => {
                              if (window.confirm(`Mark this booking as refunded?\n\nNOTE: BookFreely does NOT move money. You must return $${Number(b.downpayment_amount).toFixed(2)} to ${b.customer_name} directly (Venmo, Zelle, cash, your own Stripe, etc). This action only labels the booking as refunded.`)) act(b.id, "refund", b);
                            }} className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>Mark refunded</button>
                          )}
                        </>
                      )}
                    </div>
                    {b.is_mobile && (b.status === "confirmed" || b.status === "completed") && (
                      <ProviderMobileControls b={b} authHeader={authHeader} onChanged={load} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {acceptModal && (
        <AcceptBookingModal
          booking={acceptModal}
          authHeader={authHeader}
          onClose={() => setAcceptModal(null)}
          onAccepted={() => { setAcceptModal(null); load(); }}
        />
      )}
      <Footer />
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

async function resizeImageToDataUrl(file, maxSize = 512, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

function BusinessProfileEditor({ user, authHeader, refreshUser }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    business_name: user.business_name || "",
    business_description: user.business_description || "",
    business_category: user.business_category || "",
    contact_phone: user.contact_phone || "",
    contact_email: user.contact_email || "",
    website: user.website || "",
    profile_image_url: user.profile_image_url || "",
    address: user.address || "",
    city: user.city || "",
    state: user.state || "",
    zip_code: user.zip_code || "",
    service_radius_miles: user.service_radius_miles ?? "",
    offers_in_person: user.offers_in_person ?? true,
    offers_online: user.offers_online ?? false,
    address_visibility: user.address_visibility || "city_state",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // --- AI Category Suggestion (opt-in, mirrors the signup UX) ---
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  const loadCategoryList = async () => {
    if (categoriesLoaded) return;
    try {
      const r = await axios.get(`${API}/categories`);
      setCategoryList(r.data?.categories || []);
      setCategoriesLoaded(true);
    } catch (err) {
      // non-blocking — user can still pick via manual dropdown if it loads later
      void err;
    }
  };

  const requestCategorySuggestion = async () => {
    const desc = (form.business_description || "").trim();
    if (desc.length < 10) {
      toast.error("Please describe your business first (at least 10 characters).");
      return;
    }
    setSuggesting(true);
    setSuggestion(null);
    try {
      const r = await axios.post(`${API}/categories/suggest`, {
        business_description: desc,
        business_name: (form.business_name || "").trim(),
      });
      setSuggestion(r.data || null);
      if (!r.data?.category) {
        setShowManualPicker(true);
        loadCategoryList();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch a suggestion — please pick a category manually.");
      setShowManualPicker(true);
      loadCategoryList();
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestedCategory = () => {
    if (suggestion?.category?.name) {
      setForm((f) => ({ ...f, business_category: suggestion.category.name }));
      setShowManualPicker(false);
      toast.success(`Category set to "${suggestion.category.name}" — click Save changes to apply.`);
    }
  };

  const openManualPicker = () => {
    setShowManualPicker(true);
    loadCategoryList();
  };

  const pickCategoryManually = (name) => {
    setForm((f) => ({ ...f, business_category: name }));
    toast.success(`Category set to "${name}" — click Save changes to apply.`);
  };

  const clearChosenCategory = () => {
    setForm((f) => ({ ...f, business_category: "" }));
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 512, 0.85);
      setForm((f) => ({ ...f, profile_image_url: dataUrl }));
      toast.success("Image ready — click Save changes to apply");
    } catch (err) {
      toast.error("Could not process image");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    // Validate website up-front so users see a clear inline error
    // instead of an opaque Stripe error later during onboarding.
    if (form.website && form.website.trim()) {
      const check = validateBusinessWebsite(form.website);
      if (!check.ok) {
        toast.error(check.error);
        return;
      }
      form.website = check.normalized;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numeric / boolean fields the server expects typed
      if (payload.service_radius_miles === "" || payload.service_radius_miles === null) {
        delete payload.service_radius_miles;
      } else {
        payload.service_radius_miles = parseFloat(payload.service_radius_miles);
      }
      await axios.patch(`${API}/business/profile`, payload, { headers: authHeader() });
      await refreshUser();
      toast.success("Profile updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div id="business-profile-editor" className="mt-8 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="business-profile-editor">
      <div className="flex items-center gap-4">
        {(form.profile_image_url || user.profile_image_url) ? (
          <img src={form.profile_image_url || user.profile_image_url} alt="" className="w-16 h-16 rounded-full object-cover border" style={{ borderColor: "var(--gf-border-strong)" }} data-testid="profile-image-preview" />
        ) : (
          <div className="w-16 h-16 rounded-full border flex items-center justify-center font-display text-2xl" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)", background: "var(--gf-surface-2)" }}>
            {(user.business_name || user.full_name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Your profile</div>
          <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>{user.business_name || user.full_name}</div>
          <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
            {user.business_category}{user.website ? ` · ${user.website.replace(/^https?:\/\//, "")}` : ""}
          </div>
        </div>
        <button data-testid="edit-profile-btn" onClick={() => setOpen((v) => !v)} className="text-sm rounded-full border px-4 py-2" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
          {open ? "Close" : "Edit profile"}
        </button>
      </div>

      {open && (
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Business name">
            <input data-testid="edit-business-name" className={inputCls} style={inputStyle} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
          </Field>
          <Field label="Contact phone">
            <input data-testid="edit-contact-phone" className={inputCls} style={inputStyle} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </Field>
          <Field label="Description" full>
            <textarea
              rows={3}
              data-testid="edit-description"
              className={inputCls}
              style={inputStyle}
              value={form.business_description}
              onChange={(e) => {
                setForm({ ...form, business_description: e.target.value });
                if (suggestion) setSuggestion(null);
              }}
            />
            <AiDescriptionButtons
              value={form.business_description}
              onChange={(text) => {
                setForm({ ...form, business_description: text });
                if (suggestion) setSuggestion(null);
              }}
              testidPrefix="dashboard-ai-desc"
            />
            {/* --- AI Category Suggestion (optional) --- */}
            <div className="mt-3 space-y-3" data-testid="edit-ai-category-suggest-block">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid="edit-ai-suggest-category-btn"
                  onClick={requestCategorySuggestion}
                  disabled={suggesting || (form.business_description || "").trim().length < 10}
                  className="text-xs uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: "var(--gf-accent)", color: "var(--gf-accent)", background: "transparent" }}
                >
                  {suggesting ? "Thinking…" : "✨ Suggest Category"}
                </button>
                {!showManualPicker && (
                  <button
                    type="button"
                    data-testid="edit-ai-choose-manually-btn"
                    onClick={openManualPicker}
                    className="text-xs underline"
                    style={{ color: "var(--gf-text-muted)" }}
                  >
                    or choose a category manually
                  </button>
                )}
                {form.business_category && (
                  <div
                    data-testid="edit-ai-chosen-category-pill"
                    className="text-xs rounded-full px-3 py-1 border flex items-center gap-2"
                    style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface-2)", color: "var(--gf-text)" }}
                  >
                    <span>Category: <strong>{form.business_category}</strong></span>
                    <button
                      type="button"
                      data-testid="edit-ai-clear-category-btn"
                      onClick={clearChosenCategory}
                      className="opacity-70 hover:opacity-100"
                      aria-label="Clear chosen category"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {suggestion && suggestion.category && (
                <div
                  data-testid="edit-ai-suggestion-card"
                  className="rounded-2xl border p-4"
                  style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                      AI Suggestion · {suggestion.confidence} confidence
                    </div>
                    <div
                      data-testid="edit-ai-suggested-category-name"
                      className="font-display text-lg mt-1"
                      style={{ color: "var(--gf-text)" }}
                    >
                      {suggestion.category.name}
                    </div>
                    {suggestion.reason && (
                      <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
                        {suggestion.reason}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="edit-ai-use-suggested-btn"
                      onClick={acceptSuggestedCategory}
                      className="text-xs uppercase tracking-[0.15em] rounded-full px-4 py-2 transition-colors"
                      style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                    >
                      Use Suggested Category
                    </button>
                    <button
                      type="button"
                      data-testid="edit-ai-choose-another-btn"
                      onClick={openManualPicker}
                      className="text-xs uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors"
                      style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                    >
                      Choose Another Category
                    </button>
                  </div>
                </div>
              )}

              {suggestion && !suggestion.category && (
                <div
                  data-testid="edit-ai-suggestion-lowconf"
                  className="rounded-2xl border p-4 text-xs"
                  style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }}
                >
                  {suggestion.reason || "We couldn't confidently determine your category. Please choose one manually."}
                </div>
              )}

              {showManualPicker && (
                <div data-testid="edit-ai-manual-picker">
                  <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                    Choose a category
                  </label>
                  <select
                    data-testid="edit-ai-manual-category-select"
                    value={form.business_category || ""}
                    onChange={(e) => {
                      const name = e.target.value;
                      if (name) pickCategoryManually(name);
                    }}
                    className={inputCls}
                    style={inputStyle}
                  >
                    <option value="" disabled>
                      {categoriesLoaded ? "Select a category…" : "Loading categories…"}
                    </option>
                    {categoryList.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Field>
          <Field label="Contact email">
            <input data-testid="edit-contact-email" type="email" className={inputCls} style={inputStyle} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </Field>
          <Field label="Website">
            <input
              data-testid="edit-website"
              className={inputCls}
              style={inputStyle}
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://your-business.com"
              inputMode="url"
              type="url"
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
              Optional. Must be a full URL like <code>https://example.com</code>. Social handles or app names (e.g. &quot;instagram&quot;, &quot;discord&quot;) aren&rsquo;t valid website addresses.
            </p>
          </Field>
          <Field label="Profile picture" full>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm rounded-full border px-4 py-2 cursor-pointer hover:opacity-80 transition" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} data-testid="upload-profile-image-label">
                {uploading ? "Processing..." : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile} disabled={uploading} data-testid="upload-profile-image-input" />
              </label>
              {form.profile_image_url && (
                <button type="button" onClick={() => setForm({ ...form, profile_image_url: "" })} className="text-xs underline" style={{ color: "var(--gf-text-muted)" }} data-testid="clear-profile-image-btn">
                  Remove
                </button>
              )}
            </div>
            <input data-testid="edit-profile-image" className={`${inputCls} mt-2`} style={inputStyle} value={form.profile_image_url?.startsWith("data:") ? "" : form.profile_image_url} onChange={(e) => setForm({ ...form, profile_image_url: e.target.value })} placeholder="…or paste an image URL (https://…)" />
            <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Upload a photo from your device or paste a public image URL. Square works best.</p>
          </Field>

          <div className="sm:col-span-2 mt-2">
            <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--gf-text-muted)" }}>Service location &amp; availability</div>
            <p className="text-xs mb-3 max-w-xl" style={{ color: "var(--gf-text-muted)" }} data-testid="location-explainer">
              We require your business location so customers can find services near them, see your city/state, estimate distance, and book local providers more easily. Your exact street address will only be shown if you choose to display it or if it is needed for an in-person appointment.
            </p>
          </div>
          <Field label="Address (private)" full>
            <input data-testid="edit-address" className={inputCls} style={inputStyle} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street address (used for distance only)" />
          </Field>
          <Field label="City">
            <input data-testid="edit-city" className={inputCls} style={inputStyle} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" />
          </Field>
          <Field label="State">
            <input data-testid="edit-state" className={inputCls} style={inputStyle} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" />
          </Field>
          <Field label="ZIP code">
            <input data-testid="edit-zip" className={inputCls} style={inputStyle} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="ZIP / postal code" />
          </Field>
          <Field label="Mobile service radius (miles)">
            <input data-testid="edit-radius" type="number" min="0" max="500" className={inputCls} style={inputStyle} value={form.service_radius_miles} onChange={(e) => setForm({ ...form, service_radius_miles: e.target.value })} placeholder="e.g. 15" />
          </Field>
          <Field label="Service modes" full>
            <div className="flex gap-4 flex-wrap text-sm" style={{ color: "var(--gf-text)" }}>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="edit-offers-inperson" type="checkbox" checked={!!form.offers_in_person} onChange={(e) => setForm({ ...form, offers_in_person: e.target.checked })} />
                <span>In-person at your address</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="edit-offers-online" type="checkbox" checked={!!form.offers_online} onChange={(e) => setForm({ ...form, offers_online: e.target.checked })} />
                <span>Online / virtual</span>
              </label>
            </div>
          </Field>
          <Field label="Public address visibility" full>
            <div className="space-y-2 text-sm" style={{ color: "var(--gf-text-soft)" }} data-testid="address-visibility-options">
              {[
                { v: "full",         label: "Show full address publicly",   desc: "Street address + city + state shown on your public profile." },
                { v: "city_state",   label: "Show city & state only",       desc: "Customers see your city and state, not your street. Recommended." },
                { v: "service_area", label: "Show service area only",       desc: "Show a generic service area (e.g. 'Brooklyn area, within ~15 miles'). Hides street + exact city." },
                { v: "hidden",       label: "Hide exact street address",    desc: "Used internally for distance only — nothing shown publicly. Customers can still see you in 'near me' results." },
              ].map((opt) => (
                <label key={opt.v} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="address_visibility"
                    value={opt.v}
                    checked={form.address_visibility === opt.v}
                    onChange={() => setForm({ ...form, address_visibility: opt.v })}
                    data-testid={`address-visibility-${opt.v}`}
                    className="mt-1"
                  />
                  <span>
                    <span style={{ color: "var(--gf-text)" }}>{opt.label}</span>
                    <span className="block text-xs" style={{ color: "var(--gf-text-muted)" }}>{opt.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>

          <div className="sm:col-span-2">
            <button data-testid="save-profile-btn" onClick={save} disabled={saving} className="rounded-full font-medium px-6 py-2.5 disabled:opacity-50" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BusinessStats({ bookings, services, balance = 0 }) {
  const now = new Date();
  const todayStr = now.toDateString();
  const weekEnd = new Date(now); weekEnd.setDate(weekEnd.getDate() + 7);

  const confirmed = (bookings || []).filter((b) => ["confirmed", "completed"].includes(b.status));
  const today = confirmed.filter((b) => new Date(b.scheduled_at).toDateString() === todayStr).length;
  const thisWeek = confirmed.filter((b) => { const d = new Date(b.scheduled_at); return d >= now && d <= weekEnd; }).length;
  const pending = (bookings || []).filter((b) => b.status === "pending").length;
  const ratings = (services || []).filter((s) => (s.rating_count || 0) > 0);
  const avgRating = ratings.length ? (ratings.reduce((a, s) => a + (s.avg_rating || 0) * s.rating_count, 0) / ratings.reduce((a, s) => a + s.rating_count, 0)) : 0;
  const completedCount = (bookings || []).filter((b) => b.status === "completed").length;

  const cards = [
    { label: "Pending", value: pending, sub: pending === 1 ? "request" : "requests", accent: pending > 0 },
    { label: "Today", value: today, sub: "appointments" },
    { label: "Next 7 days", value: thisWeek, sub: "appointments" },
    { label: "Completed", value: completedCount, sub: "all-time" },
    { label: "Avg rating", value: avgRating ? avgRating.toFixed(1) : "—", sub: ratings.length ? `across ${ratings.length} services` : "no reviews yet" },
  ];
  return (
    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="business-stats">
      {cards.map((c, i) => (
        <div key={i} className="rounded-xl border p-4" style={{ borderColor: c.accent ? "var(--gf-surface-2)" : "var(--gf-border)", background: c.accent ? "var(--gf-surface-2)" : "var(--gf-surface)" }} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{c.label}</div>
          <div className="font-display text-3xl mt-1" style={{ color: c.accent ? "var(--gf-accent)" : "var(--gf-text)" }}>{c.value}</div>
          <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SubscriptionPanel({ user, authHeader, refreshUser }) {
  const [refundOpen, setRefundOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  // Iteration 56 (Feb 28 2026) — Stripe Customer Portal launcher. Scoped
  // to the panel because the outer component's `openBillingPortal` isn't
  // in scope down here.
  const openBillingPortal = async () => {
    if (!user?.stripe_customer_id) {
      toast.error("Subscribe first — no Stripe customer on file yet.");
      return;
    }
    setPortalBusy(true);
    try {
      const r = await axios.post(`${API}/subscription/portal`, {}, { headers: authHeader() });
      if (r?.data?.url) {
        window.location.assign(r.data.url);
      } else {
        toast.error("Could not open the Stripe portal");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the Stripe portal");
    } finally {
      setPortalBusy(false);
    }
  };

  if (!user) return null;

  const subActive = !!user.subscription_active;
  const willCancel = !!user.cancel_at_period_end;
  const inTrial = user.subscription_status === "trialing" && !!user.trial_end_at;
  // During a free trial, the "access ends" date is the trial end, NOT the
  // (30-day-out) monthly renewal that Stripe schedules for after the trial.
  // This is what the pill / cancel modal / notice all read from.
  const accessEndsRaw = inTrial ? user.trial_end_at : user.subscription_expires_at;
  const endsAt = accessEndsRaw ? new Date(accessEndsRaw) : null;
  const endsAtStr = endsAt ? endsAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  const request = async () => {
    if (reason.trim().length < 10) { toast.error("Please describe your reason (at least 10 characters)"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/subscription/refund-request`, { reason }, { headers: authHeader() });
      setVerdict(r.data);
      toast.success("Refund request submitted for review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMembership = async () => {
    setCancelBusy(true);
    try {
      const r = await axios.post(`${API}/business/subscription/cancel`, {}, { headers: authHeader() });
      toast.success(r.data?.message || "Membership cancellation scheduled");
      setCancelModal(false);
      refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel — please try again");
    } finally {
      setCancelBusy(false);
    }
  };

  const resumeMembership = async () => {
    try {
      const r = await axios.post(`${API}/business/subscription/resume`, {}, { headers: authHeader() });
      toast.success(r.data?.message || "Membership resumed");
      refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume — please try again");
    }
  };

  // Status pill colors + copy. Trial users get "Free trial · first charge <date>"
  // (that IS the day their subscription renews if they don't cancel). Post-trial
  // users get "Active · renews <date>". Cancelling users get "Ends <date>".
  const pill = willCancel
    ? { bg: "rgba(217,119,6,0.12)", color: "#9a6500", label: `Ends ${endsAtStr || "soon"}` }
    : subActive
      ? inTrial
        ? { bg: "var(--gf-surface)", color: "var(--gf-accent)", label: `Free trial · first charge ${endsAtStr || "soon"}` }
        : { bg: "var(--gf-surface)", color: "var(--gf-accent)", label: `Active · renews ${endsAtStr || "monthly"}` }
      : { bg: "var(--gf-bg-soft)", color: "var(--gf-text-muted)", label: "Inactive" };

  return (
    <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="subscription-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Membership</div>
          <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>
            {subActive ? `${user.subscription_tier === "yearly" ? "Annual" : "Monthly"} plan` : "No active membership"}
          </div>
          <div className="mt-2">
            <span
              className="inline-block text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-1"
              style={{ background: pill.bg, color: pill.color }}
              data-testid="subscription-status-pill"
            >
              {pill.label}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Iteration 56 (Feb 28 2026) — Stripe Customer Portal is the single
              management surface. All cancel/resume/plan-change flows live there. */}
          {!subActive && (
            <Link
              to="/business/subscribe"
              data-testid="resubscribe-link"
              className="text-sm rounded-full font-medium px-4 py-2"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              Re-subscribe
            </Link>
          )}
          {user?.stripe_customer_id && (
            <button
              data-testid="dashboard-manage-subscription-btn"
              onClick={openBillingPortal}
              disabled={portalBusy}
              className="text-sm rounded-full font-medium px-4 py-2 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {portalBusy ? "Opening…" : "Manage subscription"}
            </button>
          )}
          {subActive && (
            <Link
              to="/business/billing"
              data-testid="open-billing-page-btn"
              className="text-sm rounded-full border px-4 py-2"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Billing &amp; invoices
            </Link>
          )}
          {subActive && (
            <button data-testid="request-sub-refund-btn" onClick={() => setRefundOpen((v) => !v)} className="text-sm rounded-full border px-4 py-2" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
              {refundOpen ? "Hide refund form" : "Request refund"}
            </button>
          )}
        </div>
      </div>

      {willCancel && (
        <div data-testid="cancellation-scheduled-banner" className="mt-4 rounded-md border p-3 text-sm"
          style={{ borderColor: "rgba(217,119,6,0.5)", background: "rgba(217,119,6,0.06)", color: "var(--gf-text-soft)" }}>
          Your membership is scheduled to end on <b style={{ color: "var(--gf-text)" }}>{endsAtStr || "your next billing date"}</b>.
          You keep full access until then — no further charges. After that, your services stop appearing in search until you re-subscribe.
        </div>
      )}
      {!subActive && (
        <div className="mt-4 rounded-md border p-3 text-sm"
          style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)", color: "var(--gf-text-soft)" }}>
          You don't have an active BookFreely membership. Services are hidden from public search until you re-subscribe.
        </div>
      )}

      {refundOpen && (
        <div className="mt-4">
          <p className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
            Subscription refund requests are reviewed by a human on the BookFreely team &mdash; not by AI. Eligible reasons include duplicate charge, charge after cancellation, confirmed BookFreely outage &gt;7 days, or unauthorized/fraudulent charge. Not eligible: changed mind, didn't use it enough. You'll hear back by email once we've reviewed.
          </p>
          <textarea
            data-testid="sub-refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Describe what happened with your subscription..."
            className="mt-3 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <button data-testid="submit-sub-refund-btn" onClick={request} disabled={submitting}
            className="mt-3 rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {submitting ? "Submitting..." : "Submit for review"}
          </button>
          {verdict && (
            <div className="mt-4 rounded-md border p-3 text-sm" data-testid="sub-refund-verdict"
                 style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }}>
              <div className="font-display text-base" style={{ color: "var(--gf-text)" }}>
                Submitted for review
              </div>
              <div className="mt-2 whitespace-pre-line" style={{ color: "var(--gf-text-soft)" }}>
                {verdict.message || "Your refund request has been submitted for human review. You'll hear back by email once BookFreely's refund policy team decides the outcome."}
              </div>
            </div>
          )}
        </div>
      )}

      {cancelModal && (
        <CancelMembershipModal
          onClose={() => setCancelModal(false)}
          onConfirm={cancelMembership}
          busy={cancelBusy}
          endsAtStr={endsAtStr}
          inTrial={inTrial}
        />
      )}
    </div>
  );
}

function CancelMembershipModal({ onClose, onConfirm, busy, endsAtStr, inTrial }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="cancel-membership-modal"
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#dc2626" }}>Cancel membership</div>
        <h3 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>
          {inTrial ? "Cancel before your trial ends?" : "Cancel at the end of your billing period?"}
        </h3>
        {inTrial ? (
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-soft)" }}>
            You&rsquo;re on the free trial. If you cancel now, you keep full access until <b style={{ color: "var(--gf-text)" }}>{endsAtStr || "your trial end date"}</b> —
            the day your trial would end. <b style={{ color: "var(--gf-text)" }}>You won&rsquo;t be charged.</b>
          </p>
        ) : (
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-soft)" }}>
            Your membership stays active until <b style={{ color: "var(--gf-text)" }}>{endsAtStr || "your next billing date"}</b>.
            You&rsquo;ll keep all features and visibility until then. No further charges after that date.
          </p>
        )}

        <div className="mt-4 rounded-md border p-3 text-xs space-y-1.5"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)", color: "var(--gf-text-soft)" }}
          data-testid="cancel-impact-list"
        >
          <div><b style={{ color: "var(--gf-text)" }}>What happens after the end date:</b></div>
          <div>• Your services stop appearing in customer search.</div>
          <div>• New booking requests are blocked.</div>
          <div>• Existing bookings stay on the books — you can still complete them.</div>
          <div>• Your profile, services, and reviews are saved. Re-subscribe anytime to relist instantly.</div>
        </div>

        <div className="mt-3 text-xs" style={{ color: "var(--gf-text-muted)" }}>
          Change your mind before the end date? Just click <b style={{ color: "var(--gf-text)" }}>Resume membership</b> on this panel — no charges, no interruption.
        </div>

        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button
            data-testid="cancel-membership-back-btn"
            onClick={onClose}
            className="text-sm rounded-full border px-4 py-2"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}
          >
            Keep my membership
          </button>
          <button
            data-testid="cancel-membership-confirm-btn"
            onClick={onConfirm}
            disabled={busy}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "#dc2626", color: "#fff" }}
          >
            {busy ? "Working…" : "Cancel at next billing date"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UpcomingList({ bookings }) {
  const upcoming = (bookings || [])
    .filter((b) => ["confirmed", "completed", "pending"].includes(b.status))
    .map((b) => ({ ...b, _d: new Date(b.scheduled_at) }))
    .filter((b) => b._d >= new Date(Date.now() - 86400000))
    .sort((a, b) => a._d - b._d)
    .slice(0, 8);

  // Group by day
  const groups = {};
  upcoming.forEach((b) => {
    const k = b._d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    (groups[k] = groups[k] || []).push(b);
  });

  if (upcoming.length === 0) {
    return (
      <div className="mt-3 text-sm p-6 border rounded-xl" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
        No upcoming appointments. New confirmed bookings will appear here.
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3" data-testid="upcoming-list">
      {Object.entries(groups).map(([day, items]) => (
        <div key={day} className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>{day}</div>
          <ul className="mt-2 space-y-2">
            {items.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <div style={{ color: "var(--gf-text)" }}>
                    {b._d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} — {b.service_title}
                  </div>
                  <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>with {b.customer_name}</div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] border rounded-full px-2 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>{b.status}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------- Accept booking modal with welcome message + templates ----------
function AcceptBookingModal({ booking, authHeader, onClose, onAccepted }) {
  const [templates, setTemplates] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/business/message-templates`, { headers: authHeader() })
      .then((r) => setTemplates([...(r.data?.defaults || []), ...(r.data?.custom || [])]))
      .catch(() => setTemplates([]));
  }, [authHeader]);

  const accept = async (withMessage) => {
    setBusy(true);
    try {
      const payload = { action: "accept" };
      if (withMessage && text.trim()) payload.message = text.trim();
      await axios.post(`${API}/bookings/${booking.id}/action`, payload, { headers: authHeader() });
      toast.success(withMessage && text.trim() ? "Booking accepted + message sent" : "Booking accepted");
      onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="accept-booking-modal">
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Accept booking</div>
        <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>
          {booking.service_title}
        </h3>
        <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
          with <b style={{ color: "var(--gf-text)" }}>{booking.customer_name}</b> · {new Date(booking.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>

        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Quick templates</div>
          <div className="flex flex-wrap gap-2" data-testid="accept-templates">
            {templates.map((t) => (
              <button
                key={t.key}
                data-testid={`tpl-${t.key}`}
                onClick={() => setText(t.text)}
                className="text-xs rounded-full border px-3 py-1.5 text-left max-w-full"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                {t.title}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Welcome / prep message (optional)</label>
          <textarea
            data-testid="accept-message-textarea"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            placeholder="Looking forward to your appointment! Anything they should know — directions, what to bring, arrive-early..."
          />
        </div>

        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button
            data-testid="accept-cancel-btn"
            onClick={onClose}
            className="text-sm rounded-full border px-4 py-2"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}
          >
            Cancel
          </button>
          <button
            data-testid="accept-only-btn"
            onClick={() => accept(false)}
            disabled={busy}
            className="text-sm rounded-full border px-4 py-2 disabled:opacity-50"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            Accept without message
          </button>
          <button
            data-testid="accept-with-message-btn"
            onClick={() => accept(true)}
            disabled={busy || !text.trim()}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            {busy ? "Sending…" : "Accept and send message"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Business analytics card ----------
const RANGE_OPTIONS = [
  ["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"],
  ["this_month", "This month"], ["3m", "3 months"], ["6m", "6 months"], ["12m", "12 months"],
  ["custom", "Custom"],
];

function fmtBucket(key) {
  if (!key) return "";
  const parts = key.split("-");
  if (parts.length === 2) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
    return d.toLocaleDateString([], { month: "short", year: "2-digit" });
  }
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function TrendPill({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium rounded-full px-1.5 py-0.5"
      style={{ color: up ? "#16a34a" : "#dc2626", background: up ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)" }}>
      {up ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
}

function MetricCard({ label, value, sub, trend, testid, accent }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
        {trend !== undefined && <TrendPill pct={trend} />}
      </div>
      <div className="font-display text-xl sm:text-2xl mt-0.5" style={{ color: accent || "var(--gf-text)" }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{sub}</div>}
    </div>
  );
}

function AnalyticsPanel({ authHeader, user }) {
  const [rangeKey, setRangeKey] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [showEarnings, setShowEarnings] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const query = React.useMemo(() => {
    if (rangeKey === "custom") {
      if (!custom.start || !custom.end) return null;
      return `range=custom&start=${custom.start}&end=${custom.end}`;
    }
    return `range=${rangeKey}`;
  }, [rangeKey, custom.start, custom.end]);

  useEffect(() => {
    if (!query) return;
    let alive = true;
    const fetchAll = () => {
      Promise.all([
        axios.get(`${API}/business/analytics/summary?${query}`, { headers: authHeader() }),
        axios.get(`${API}/business/earnings?${query}`, { headers: authHeader() }),
      ]).then(([sum, earn]) => {
        if (!alive) return;
        setData(sum.data);
        setEarnings(earn.data);
        setUpdatedAt(new Date());
      }).catch(() => {});
    };
    fetchAll();
    // Near real-time: refresh analytics every 30s while the dashboard is open.
    const t = setInterval(fetchAll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [query, authHeader, user?.id]);

  const f = data?.financials || {};
  const c = data?.counts || {};
  const cust = data?.customers || {};
  const t = data?.trend || {};

  return (
    <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="analytics-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Analytics</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Your performance</h2>
        </div>
        <div className="text-xs" style={{ color: "var(--gf-text-muted)" }} data-testid="analytics-updated">
          {updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Loading…"}
        </div>
      </div>

      {/* Date range selector */}
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="analytics-range-selector">
        {RANGE_OPTIONS.map(([key, lbl]) => (
          <button key={key} type="button" data-testid={`analytics-range-${key}`}
            onClick={() => setRangeKey(key)}
            className="rounded-full border px-3 py-1 text-xs transition-colors"
            style={rangeKey === key
              ? { background: "var(--gf-accent)", borderColor: "var(--gf-accent)", color: "#fff" }
              : { borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
            {lbl}
          </button>
        ))}
      </div>
      {rangeKey === "custom" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>
          <input type="date" data-testid="analytics-custom-start" value={custom.start}
            onChange={(e) => setCustom((s) => ({ ...s, start: e.target.value }))}
            className="rounded-md border px-2 py-1 bg-transparent" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          <span>to</span>
          <input type="date" data-testid="analytics-custom-end" value={custom.end}
            onChange={(e) => setCustom((s) => ({ ...s, end: e.target.value }))}
            className="rounded-md border px-2 py-1 bg-transparent" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
        </div>
      )}

      {!data ? (
        <div className="mt-6 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="analytics-loading">
          {rangeKey === "custom" && !query ? "Pick a start and end date." : "Loading analytics…"}
        </div>
      ) : (
        <>
          {/* Appointments */}
          <div className="mt-5 text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Appointments</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard label="Booked in period" value={c.booked_in_period ?? 0} trend={t.booked}
              sub="New bookings placed" testid="analytics-booked" />
            <MetricCard label="Scheduled in period" value={c.scheduled_in_period ?? 0}
              sub="Appointments due" testid="analytics-scheduled" />
            <MetricCard label="Completed" value={c.completed ?? 0} trend={t.completed} testid="analytics-completed" />
            <MetricCard label="Upcoming" value={c.upcoming ?? 0} testid="analytics-upcoming" />
            <MetricCard label="Cancelled" value={c.cancelled ?? 0} testid="analytics-cancelled" />
            <MetricCard label="No-shows" value={c.no_show ?? 0} testid="analytics-noshow" />
          </div>

          {/* Customers */}
          <div className="mt-5 text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Customers</div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <MetricCard label="Unique" value={cust.unique ?? 0} trend={t.unique_customers}
              sub="Booked in period" testid="analytics-unique" />
            <MetricCard label="New" value={cust.new ?? 0} sub="First-time" testid="analytics-new" />
            <MetricCard label="Returning" value={cust.returning ?? 0} sub="Booked before" testid="analytics-returning" />
          </div>

          {/* Money */}
          <div className="mt-5 text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Earnings & payments</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard label="Gross service revenue" value={money(f.gross_cents)} trend={t.gross_cents}
              sub="Completed appointments" testid="analytics-gross" />
            <MetricCard label="Net business earnings" value={money(f.net_cents)} trend={t.net_cents}
              accent="var(--gf-accent)" sub="After BookFreely fees" testid="analytics-net" />
            <MetricCard label="BookFreely fees" value={money((f.acquisition_fee_cents || 0) + (f.platform_fee_cents || 0))}
              sub="Commission on service price" testid="analytics-fees" />
            <MetricCard label="Refunds" value={money(f.refund_cents)} testid="analytics-refunds" />
            <MetricCard label="Pending payments" value={money(f.pending_payments_cents)}
              sub="Balance not yet charged" testid="analytics-pending-payments" />
            <MetricCard label="Pending transfers / payouts" value={money(f.pending_transfers_cents)}
              sub="Awaiting release to your Stripe" testid="analytics-pending-transfers" />
          </div>
          <p className="mt-2 text-[10px]" style={{ color: "var(--gf-text-muted)" }}>
            The customer Booking &amp; Processing Fee is BookFreely's and is never counted as your revenue.
            Pending balances are current totals, not limited to the selected period.
          </p>

          {/* Charts */}
          {Array.isArray(data.timeseries) && data.timeseries.length > 0 && (
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid="analytics-chart-bookings">
                <div className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Bookings over time</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={data.timeseries} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gf-border)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtBucket} tick={{ fontSize: 10, fill: "var(--gf-text-muted)" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--gf-text-muted)" }} />
                    <Tooltip labelFormatter={fmtBucket} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="booked" name="Booked" fill="var(--gf-accent)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid="analytics-chart-revenue">
                <div className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Revenue over time</div>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={data.timeseries} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grossG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--gf-accent)" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="var(--gf-accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gf-border)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtBucket} tick={{ fontSize: 10, fill: "var(--gf-text-muted)" }} />
                    <YAxis tickFormatter={(v) => `$${Math.round(v / 100)}`} tick={{ fontSize: 10, fill: "var(--gf-text-muted)" }} />
                    <Tooltip labelFormatter={fmtBucket} formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="gross_cents" name="Gross" stroke="var(--gf-accent)" fill="url(#grossG)" strokeWidth={2} />
                    <Area type="monotone" dataKey="net_cents" name="Net" stroke="#16a34a" fill="transparent" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top services */}
          {Array.isArray(data.top_services) && data.top_services.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Most booked services</div>
              <ul className="space-y-1.5" data-testid="analytics-top-services">
                {data.top_services.map((s) => (
                  <li key={s.service_id} className="flex items-center justify-between text-sm rounded-md border px-3 py-2"
                    style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
                    <span style={{ color: "var(--gf-text)" }}>{s.title}</span>
                    <span style={{ color: "var(--gf-text-muted)" }} className="text-xs">{s.count} {s.count === 1 ? "booking" : "bookings"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Per-booking earnings transparency */}
          <div className="mt-5">
            <button type="button" data-testid="analytics-earnings-toggle"
              onClick={() => setShowEarnings((v) => !v)}
              className="text-xs rounded-full border px-3 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
              {showEarnings ? "Hide" : "Show"} per-booking earnings ({earnings?.count ?? 0})
            </button>
            {showEarnings && (earnings?.rows?.length ? (
              <div className="mt-3 overflow-x-auto rounded-xl border" style={{ borderColor: "var(--gf-border)" }} data-testid="analytics-earnings-table">
                <table className="w-full text-xs" style={{ color: "var(--gf-text)" }}>
                  <thead>
                    <tr style={{ color: "var(--gf-text-muted)" }} className="text-left">
                      <th className="px-3 py-2 font-medium">Service</th>
                      <th className="px-3 py-2 font-medium">Customer</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium text-right">Price</th>
                      <th className="px-3 py-2 font-medium text-right">Paid</th>
                      <th className="px-3 py-2 font-medium text-right">Due</th>
                      <th className="px-3 py-2 font-medium text-right">BookFreely fee</th>
                      <th className="px-3 py-2 font-medium text-right">Net</th>
                      <th className="px-3 py-2 font-medium">Transfer</th>
                      <th className="px-3 py-2 font-medium text-right">Refund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.rows.map((r) => (
                      <tr key={r.booking_id} className="border-t" style={{ borderColor: "var(--gf-border)" }} data-testid={`earnings-row-${r.booking_id}`}>
                        <td className="px-3 py-2">{r.service_title}</td>
                        <td className="px-3 py-2">{r.customer_name}</td>
                        <td className="px-3 py-2 capitalize">{r.status}</td>
                        <td className="px-3 py-2 text-right">{money(r.service_price_cents)}</td>
                        <td className="px-3 py-2 text-right">{money(r.paid_cents)}</td>
                        <td className="px-3 py-2 text-right">{money(r.due_cents)}</td>
                        <td className="px-3 py-2 text-right">{money(r.bookfreely_fee_cents)}{r.is_acquisition ? " *" : ""}</td>
                        <td className="px-3 py-2 text-right" style={{ color: "var(--gf-accent)" }}>{money(r.net_earnings_cents)}</td>
                        <td className="px-3 py-2 capitalize">{r.transfer_status}</td>
                        <td className="px-3 py-2 text-right">{r.refund_cents ? money(r.refund_cents) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-3 text-xs" style={{ color: "var(--gf-text-muted)" }}>No bookings in this period.</div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
      <div className="font-display text-2xl mt-0.5" style={{ color: "var(--gf-text)" }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{sub}</div>}
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
const _StatRef = Stat;


// ---------- Account settings (pause / deactivate / delete / export) ----------
function AccountSettingsPanel({ user, authHeader, refreshUser }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const pause = async () => {
    setBusy("pause");
    try {
      const r = await axios.post(`${API}/business/bookings/${user.bookings_paused ? "resume" : "pause"}`, {}, { headers: authHeader() });
      toast.success(r.data.bookings_paused ? "New bookings paused" : "Bookings resumed");
      refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const reactivate = async () => {
    setBusy("reactivate");
    try {
      await axios.post(`${API}/business/account/reactivate`, {}, { headers: authHeader() });
      toast.success("Account reactivated");
      refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const r = await axios.get(`${API}/business/account/export`, { headers: authHeader() });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookfreely-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const status = user.account_status || "active";
  const deleting = status === "deleting";
  const deactivated = status === "deactivated";

  return (
    <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="account-settings-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Account settings</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Pause, deactivate, or delete</h2>
          <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--gf-text-muted)" }}>
            Step away from BookFreely without losing your data, or schedule a permanent deletion. Some booking and payment records may be kept only as long as required for tax, accounting, payment reconciliation, fraud prevention, or other legal obligations.
          </p>
        </div>
        <button
          data-testid="account-settings-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-sm rounded-full border px-4 py-2"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
        >
          {open ? "Close" : "Open settings"}
        </button>
      </div>

      {deleting && (
        <div data-testid="deleting-banner" className="mt-4 rounded-md border p-3 text-sm"
          style={{ borderColor: "#7c2d12", background: "rgba(124,45,18,0.06)", color: "var(--gf-text-soft)" }}>
          <b style={{ color: "var(--gf-text)" }}>Your account is scheduled for deletion</b>
          {user.deletion_scheduled_at && (
            <> on <b style={{ color: "var(--gf-text)" }}>{new Date(user.deletion_scheduled_at).toLocaleDateString()}</b>.</>
          )}{" "}
          Profile and services are hidden. You can recover within the 30-day grace period.
          <div className="mt-2">
            <button
              data-testid="recover-account-btn"
              onClick={reactivate}
              disabled={busy === "reactivate"}
              className="text-xs rounded-full px-4 py-2 font-medium disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy === "reactivate" ? "Recovering…" : "Recover account"}
            </button>
          </div>
        </div>
      )}

      {deactivated && !deleting && (
        <div data-testid="deactivated-banner" className="mt-4 rounded-md border p-3 text-sm"
          style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)", color: "var(--gf-text-soft)" }}>
          <b style={{ color: "var(--gf-text)" }}>Your account is deactivated.</b> Your profile is hidden from public search.
          <div className="mt-2">
            <button
              data-testid="reactivate-account-btn"
              onClick={reactivate}
              disabled={busy === "reactivate"}
              className="text-xs rounded-full px-4 py-2 font-medium disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy === "reactivate" ? "Reactivating…" : "Reactivate account"}
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="account-settings-controls">
          {/* Pause */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Pause</div>
            <div className="font-display text-base mt-1" style={{ color: "var(--gf-text)" }}>
              {user.bookings_paused ? "Bookings paused" : "Pause new bookings"}
            </div>
            <p className="text-xs mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
              Hide booking buttons temporarily. Profile + services stay public. Re-enable anytime.
            </p>
            <button
              data-testid="pause-toggle-btn"
              onClick={pause}
              disabled={busy === "pause" || deleting || deactivated}
              className="text-xs rounded-full px-4 py-2 font-medium disabled:opacity-50"
              style={user.bookings_paused
                ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)" }
                : { border: "1px solid var(--gf-border-strong)", color: "var(--gf-text)", background: "transparent" }}
            >
              {busy === "pause" ? "…" : user.bookings_paused ? "Resume bookings" : "Pause bookings"}
            </button>
          </div>
          {/* Deactivate */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Deactivate</div>
            <div className="font-display text-base mt-1" style={{ color: "var(--gf-text)" }}>Hide from public</div>
            <p className="text-xs mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
              Profile and services hidden from search. Account fully recoverable anytime.
            </p>
            <button
              data-testid="deactivate-btn"
              onClick={() => setDeactivateModal(true)}
              disabled={deleting || deactivated}
              className="text-xs rounded-full border px-4 py-2 disabled:opacity-50"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Deactivate account
            </button>
          </div>
          {/* Delete */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#dc2626" }}>Delete</div>
            <div className="font-display text-base mt-1" style={{ color: "var(--gf-text)" }}>Schedule deletion</div>
            <p className="text-xs mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
              30-day grace period. Booking and payment records are retained internally for tax + fraud-prevention.
            </p>
            <button
              data-testid="delete-btn"
              onClick={() => setDeleteModal(true)}
              disabled={deleting}
              className="text-xs rounded-full border px-4 py-2 disabled:opacity-50"
              style={{ borderColor: "#dc2626", color: "#dc2626" }}
            >
              {deleting ? "Deletion scheduled" : "Delete account"}
            </button>
          </div>
          <div className="sm:col-span-3 flex items-center justify-between flex-wrap gap-3 rounded-xl border p-4"
            style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface-2, var(--gf-surface))" }}>
            <div>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Export your data</div>
              <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
                Download a JSON copy of your profile, services, bookings, messages, payments, and ledger.
              </p>
            </div>
            <button
              data-testid="export-btn"
              onClick={exportData}
              disabled={busy === "export"}
              className="text-xs rounded-full px-4 py-2 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy === "export" ? "Preparing…" : "Download data"}
            </button>
          </div>
        </div>
      )}

      {deactivateModal && (
        <PasswordConfirmModal
          title="Deactivate account"
          confirmLabel="Deactivate account"
          warning="Your business will be hidden from public search and bookings will be disabled. You can reactivate anytime."
          onClose={() => setDeactivateModal(false)}
          onConfirm={async (password) => {
            await axios.post(`${API}/business/account/deactivate`, { password }, { headers: authHeader() });
            toast.success("Account deactivated");
            setDeactivateModal(false);
            refreshUser();
          }}
        />
      )}
      {deleteModal && (
        <DeleteAccountModal
          onClose={() => setDeleteModal(false)}
          onConfirmed={() => { setDeleteModal(false); refreshUser(); }}
          authHeader={authHeader}
        />
      )}
    </div>
  );
}

function PasswordConfirmModal({ title, confirmLabel, warning, onClose, onConfirm }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!password) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      await onConfirm(password);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="password-confirm-modal">
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Confirm with password</div>
        <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>{title}</h3>
        {warning && <p className="text-xs mt-2" style={{ color: "var(--gf-text-muted)" }}>{warning}</p>}
        <input
          data-testid="confirm-password-input"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your password"
          className="mt-4 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm rounded-full border px-4 py-2" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>Cancel</button>
          <button data-testid="confirm-password-submit" onClick={go} disabled={busy}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "#dc2626", color: "#fff" }}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteAccountModal({ onClose, onConfirmed, authHeader }) {
  const REASON_OPTIONS = [
    { value: "", label: "Choose a reason..." },
    { value: "expensive", label: "Subscription is too expensive" },
    { value: "no_bookings", label: "Not getting enough bookings" },
    { value: "switching", label: "Switching platforms" },
    { value: "closing", label: "Closing my business" },
    { value: "technical", label: "Technical issues" },
    { value: "other", label: "Other" },
  ];
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  // Retention offer (Phase 2). Server returns {retention_offer: {days:7,...}}
  // if reason=expensive & first attempt. We flip to an offer screen instead of
  // deleting immediately.
  const [retentionOffer, setRetentionOffer] = useState(null);

  const submit = async ({ acceptOffer = null } = {}) => {
    if (!password) { toast.error("Enter your password"); return; }
    if (!confirm) { toast.error("Please tick the confirmation"); return; }
    if (!reason) { toast.error("Please pick a reason"); return; }
    setBusy(true);
    try {
      const body = { password, reason, note, confirm: true };
      if (acceptOffer !== null) body.accept_retention_offer = acceptOffer;
      const r = await axios.post(`${API}/business/account/delete`, body, { headers: authHeader() });
      if (r.data.retention_offer && acceptOffer === null) {
        // First-time cost objection — pause and show the offer.
        setRetentionOffer(r.data.retention_offer);
        return;
      }
      if (r.data.retention_offer_accepted) {
        toast.success(`${r.data.extension_days} days added on the house — your subscription is extended.`);
        onConfirmed();
        return;
      }
      toast.success("Account scheduled for deletion — 30-day grace period started");
      onConfirmed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const acceptOffer = () => submit({ acceptOffer: true });
  const declineOffer = () => submit({ acceptOffer: false });

  // ---- Retention offer screen (only shown when reason=expensive and it's the first attempt) ----
  if (retentionOffer) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="retention-offer-modal">
        <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6"
          style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>One quick thought</div>
          <h3 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>
            Would you like {retentionOffer.days} more days on us?
          </h3>
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-soft)" }}>
            {retentionOffer.message || `We\u2019d hate to see you go. Would you like ${retentionOffer.days} more days of BookFreely at no charge?`}
          </p>
          <p className="text-[11px] mt-2" style={{ color: "var(--gf-text-muted)" }}>
            This offer is a one-time gift and won&apos;t appear again on future deletion requests.
          </p>
          <div className="mt-5 flex gap-2 justify-end">
            <button
              type="button"
              data-testid="retention-decline-btn"
              onClick={declineOffer}
              disabled={busy}
              className="text-sm rounded-full border px-4 py-2 disabled:opacity-50"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}
            >
              No thanks — continue deletion
            </button>
            <button
              type="button"
              data-testid="retention-accept-btn"
              onClick={acceptOffer}
              disabled={busy}
              className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy ? "Applying…" : `Yes, add ${retentionOffer.days} free days`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="delete-account-modal">
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#dc2626" }}>Delete account</div>
        <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>We&apos;re sorry to see you leaving BookFreely.</h3>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text-soft)" }}>
          Deleting your account will hide your business, remove services, disable bookings, and cancel your subscription. You have <b style={{ color: "var(--gf-text)" }}>30 days</b> to recover before deletion becomes permanent. Booking history, invoices, and payment records are retained internally for support, tax, Stripe, and fraud-prevention purposes.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Reason for leaving</label>
            <select
              data-testid="delete-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} style={{ color: "#000" }}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Anything else? <span className="opacity-70 normal-case tracking-normal text-[10px]">(optional)</span></label>
            <textarea
              data-testid="delete-note-input"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell us what we could do better..."
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Confirm with password</label>
            <input
              data-testid="delete-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: "var(--gf-text-soft)" }}>
            <input
              data-testid="delete-confirm-checkbox"
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              className="mt-0.5"
              style={{ accentColor: "#dc2626" }}
            />
            <span>I understand my account will be deactivated and scheduled for permanent deletion in 30 days.</span>
          </label>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm rounded-full border px-4 py-2" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>Cancel</button>
          <button
            data-testid="delete-permanently-btn"
            onClick={() => submit()}
            disabled={busy || !password || !confirm || !reason}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "#dc2626", color: "#fff" }}
          >
            {busy ? "Working…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ProviderMobileControls({ b, authHeader, onChanged }) {
  const [pinInput, setPinInput] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [contact, setContact] = React.useState(null);

  const status = b.provider_status || (b.status === "completed" ? "completed" : "confirmed");

  const setStatus = async (newStatus) => {
    if (newStatus === "in_progress") {
      toast.info("Verify the customer's PIN to start the appointment.");
      return;
    }
    setBusy(newStatus);
    try {
      await axios.post(`${API}/bookings/${b.id}/provider-status`, { status: newStatus }, { headers: authHeader() });
      toast.success(`Status: ${newStatus.replace("_", " ")}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy("");
    }
  };

  const verifyPin = async () => {
    if (pinInput.trim().length !== 6) { toast.error("Enter the 6-digit PIN"); return; }
    setBusy("verify-pin");
    try {
      await axios.post(`${API}/bookings/${b.id}/verify-pin`, { pin: pinInput.trim() }, { headers: authHeader() });
      toast.success("PIN verified — appointment can start");
      setPinInput("");
      // After successful verification advance to in_progress automatically
      await axios.post(`${API}/bookings/${b.id}/provider-status`, { status: "in_progress" }, { headers: authHeader() });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN mismatch");
    } finally {
      setBusy("");
    }
  };

  const revealContact = async () => {
    setBusy("reveal");
    try {
      const r = await axios.get(`${API}/bookings/${b.id}/customer-contact`, { headers: authHeader() });
      setContact(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load contact");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid={`provider-mobile-${b.id}`} className="mt-4 rounded-lg border p-3" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Mobile / in-home flow</div>
        <div className="text-[10px]" style={{ color: "var(--gf-text-muted)" }}>Status: <b style={{ color: "var(--gf-text)" }}>{status.replace("_", " ")}</b></div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {status === "confirmed" && (
          <button data-testid={`prov-on-way-${b.id}`} onClick={() => setStatus("on_the_way")} disabled={!!busy}
                  className="text-xs rounded-full border px-3 py-1.5 disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            {busy === "on_the_way" ? "…" : "I'm on the way"}
          </button>
        )}
        {status === "on_the_way" && (
          <button data-testid={`prov-arrived-${b.id}`} onClick={() => setStatus("arrived")} disabled={!!busy}
                  className="text-xs rounded-full border px-3 py-1.5 disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            {busy === "arrived" ? "…" : "I've arrived"}
          </button>
        )}
        {status === "arrived" && !b.pin_verified_at && (
          <div className="flex items-center gap-2">
            <input
              data-testid={`pin-input-${b.id}`}
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              maxLength={6}
              placeholder="Customer PIN"
              className="bg-transparent border rounded-md px-2 py-1 text-sm tracking-[4px] font-mono w-32 outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <button data-testid={`pin-verify-${b.id}`} onClick={verifyPin} disabled={busy === "verify-pin"}
                    className="text-xs rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {busy === "verify-pin" ? "…" : "Verify PIN"}
            </button>
          </div>
        )}
        {status === "in_progress" && (
          <button data-testid={`prov-complete-${b.id}`} onClick={() => setStatus("completed")} disabled={!!busy}
                  className="text-xs rounded-full border px-3 py-1.5 disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            {busy === "completed" ? "…" : "Finish appointment"}
          </button>
        )}
        <button data-testid={`reveal-contact-${b.id}`} onClick={revealContact} disabled={busy === "reveal"}
                className="text-xs rounded-full border px-3 py-1.5 ml-auto disabled:opacity-50"
                style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
          {busy === "reveal" ? "…" : (contact ? "Refresh contact" : "Show customer contact")}
        </button>
      </div>
      {contact && (
        <div data-testid={`contact-block-${b.id}`} className="mt-3 text-xs rounded-md border p-3 space-y-1"
             style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg)" }}>
          <div><b style={{ color: "var(--gf-text)" }}>Name:</b> {contact.customer_name}</div>
          <div><b style={{ color: "var(--gf-text)" }}>Phone:</b> {contact.customer_phone || "—"}</div>
          {contact.address ? (
            <div><b style={{ color: "var(--gf-text)" }}>Address:</b> {contact.address}, {contact.city}, {contact.state} {contact.zip_code}</div>
          ) : contact.address_will_be_visible_at ? (
            <div style={{ color: "var(--gf-text-muted)" }}>Address becomes visible at <b style={{ color: "var(--gf-text)" }}>{new Date(contact.address_will_be_visible_at).toLocaleString()}</b> (2h before the appointment).</div>
          ) : null}
          {contact.notes && <div><b style={{ color: "var(--gf-text)" }}>Notes:</b> {contact.notes}</div>}
          <div className="text-[10px] mt-2" style={{ color: "var(--gf-text-muted)" }}>This access is audit-logged. Use only for this booking.</div>
        </div>
      )}
    </div>
  );
}

